const CHANNEL_PLACEHOLDER = '0';
const DEFAULT_POLL_INTERVAL = 100;
const DEFAULT_TIMEOUT = 2500;

// 登录页专用热江渠道判断（规则与 ElectronHead/config 保持一致，独立维护）
const LOGIN_RJ_CHANNEL_CODES = ['202706', '10001', '4227'] as const;
const LOGIN_RJ_CHANNEL_RANGE_MIN = 80000;
const LOGIN_RJ_CHANNEL_RANGE_MAX = 89999;

function isLoginRJChannelRange(channel: string): boolean {
  const channelNumber = Number(channel);
  return channelNumber >= LOGIN_RJ_CHANNEL_RANGE_MIN && channelNumber <= LOGIN_RJ_CHANNEL_RANGE_MAX;
}

function isLoginRJChannelByCode(channel: string): boolean {
  return !!channel && (LOGIN_RJ_CHANNEL_CODES.some(code => channel.includes(code)) || isLoginRJChannelRange(channel));
}

export interface LoginChannelState {
  isRJ: boolean;
  channel: string | null;
  version?: string | null;
  resolved: boolean;
}

interface GetVersionResponse {
  data?: string;
  message?: string;
  status?: string;
}

/** 从 getVersion 返回值解析渠道号，如 5.0.4.10002 → 10002 */
export function parseChannelFromVersionResponse(response: GetVersionResponse | null | undefined): string | null {
  const versionStr = response && typeof response.data === 'string' ? response.data : null;
  if (!versionStr) {
    return null;
  }
  const parts = versionStr.split('.');
  const channel = parts[parts.length - 1];
  return channel || null;
}

export function buildLoginChannelStateFromVersion(response: GetVersionResponse): LoginChannelState {
  const version = typeof response.data === 'string' ? response.data : null;
  const channel = parseChannelFromVersionResponse(response);
  return {
    isRJ: channel ? isLoginRJChannelByCode(channel) : false,
    channel,
    version,
    resolved: true
  };
}

export function isLoginChannelReady(): boolean {
  const channel = localStorage.getItem('channel');
  if (channel === null || channel === '' || channel === CHANNEL_PLACEHOLDER) {
    return false;
  }
  return true;
}

/** Web 兜底：仍从 localStorage.channel 读取 */
export function buildLoginChannelStateFromStorage(): LoginChannelState {
  const channel = localStorage.getItem('channel');
  return {
    isRJ: channel ? isLoginRJChannelByCode(channel) : false,
    channel,
    resolved: true
  };
}

let cachedState: LoginChannelState | null = null;
const listeners = new Set<(state: LoginChannelState) => void>();

export function getCachedLoginChannelState(): LoginChannelState | null {
  return cachedState;
}

export function resetLoginChannelState(): void {
  cachedState = null;
}

export function emitLoginChannelReady(state: LoginChannelState): void {
  cachedState = state;
  listeners.forEach(listener => listener(state));
}

export function subscribeLoginChannelReady(listener: (state: LoginChannelState) => void): () => void {
  if (cachedState && cachedState.resolved) {
    listener(cachedState);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function startLoginChannelPollingFallback(
  onResolved?: (state: LoginChannelState) => void,
  options?: { pollInterval?: number; timeout?: number }
): () => void {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL;
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const startTime = Date.now();
  let stopped = false;
  let timerId: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    stopped = true;
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const finish = (state: LoginChannelState) => {
    if (stopped) {
      return;
    }
    stop();
    emitLoginChannelReady(state);
    if (onResolved) {
      onResolved(state);
    }
  };

  const tryResolve = () => {
    if (stopped) {
      return;
    }

    if (isLoginChannelReady()) {
      finish(buildLoginChannelStateFromStorage());
      return;
    }

    if (Date.now() - startTime >= timeout) {
      finish({
        isRJ: false,
        channel: localStorage.getItem('channel'),
        resolved: true
      });
    }
  };

  tryResolve();
  if (!stopped) {
    timerId = setInterval(tryResolve, pollInterval);
  }

  return stop;
}
